// Tiny in-app i18n. No runtime dep — a flat string table per language plus
// a `t()` helper. English is the canonical template; tests assert that every
// supported language has an explicit translation for every English key.
// Adding a language: add the code to LANGS, fill in the dictionary below and
// the completion table, and the picker UI surfaces it automatically.

export type Lang =
  | 'en' | 'zh' | 'hi' | 'es' | 'fr' | 'ar' | 'bn' | 'ru' | 'pt' | 'id' | 'de' | 'ja'
  | 'tr' | 'vi' | 'th' | 'ko' | 'it'

// Listed roughly by global speaker count, plus high-value non-English markets.
// Each entry shows the native name so it's recognisable in any UI language.
export const LANGS: { code: Lang; label: string; rtl?: boolean }[] = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية', rtl: true },
  { code: 'bn', label: 'বাংলা' },
  { code: 'ru', label: 'Русский' },
  { code: 'pt', label: 'Português' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ja', label: '日本語' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'th', label: 'ไทย' },
  { code: 'ko', label: '한국어' },
  { code: 'it', label: 'Italiano' },
]

export function isRTL(lang: Lang): boolean {
  return !!LANGS.find((l) => l.code === lang)?.rtl
}

const STORAGE_KEY = 'pdfhelper.lang'
const SUPPORTED = new Set<Lang>(LANGS.map((l) => l.code))

export function detectLang(): Lang {
  // Manual override wins.
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Lang | null
    if (stored && SUPPORTED.has(stored)) return stored
  } catch { /* ignore */ }
  // Fall back to browser locale, taking just the language subtag.
  const nav = typeof navigator !== 'undefined' ? navigator.language || '' : ''
  const code = nav.slice(0, 2).toLowerCase() as Lang
  return SUPPORTED.has(code) ? code : 'en'
}

export function persistLang(lang: Lang) {
  try { localStorage.setItem(STORAGE_KEY, lang) } catch { /* ignore */ }
}

// Each translation key maps to one short string per language. Keep the keys
// terse and grouped logically by where they appear in the UI. English is the
// canonical source template.
export const baseTranslations = {
  en: {
    // Toolbar
    'tb.open':           'Open PDF',
    'tb.pages':          'Pages',
    'tb.help':           'Help',
    'tb.merge_pdf':      'Merge another PDF',
    'tb.merge_at_start': 'Add to start',
    'tb.merge_at_end':   'Add to end',
    'tb.add_text':       'Add text',
    'tb.edit_text':      'Edit text',
    'tb.redact':         'Redact',
    'tb.add_signature':  'Add signature',
    'tb.add_image':      'Add image (session only)',
    'tb.watermark':      'Watermark',
    'tb.draw':           'Draw',
    'tb.select':         'Select',
    'tb.pen_settings':   'Pen settings',
    'draw.tool':         'Tool',
    'draw.tool.pen':     'Pen',
    'draw.tool.marker':  'Marker',
    'draw.tool.highlighter': 'Highlighter',
    'draw.tool.line':    'Line',
    'draw.tool.arrow':   'Arrow',
    'draw.tool.eraser':  'Eraser',
    'draw.presets':      'Presets',
    'draw.preset.pen':   'Pen',
    'draw.preset.red_pen': 'Red pen',
    'draw.preset.marker': 'Marker',
    'draw.preset.highlighter': 'Highlighter',
    'draw.undo_stroke':  'Undo stroke',
    'tb.insert_date':    "Insert today's date",
    'tb.profile':        'Profile',
    'tb.undo':           'Undo',
    'tb.clear_all':      'Clear all',
    'tb.download':       'Download',
    'tb.download_options': 'Download options',
    'tb.compress_pdf':   'Make PDF smaller',
    'tb.compress_hint':  'Rasterise pages to JPEG. Smaller file, but text is no longer selectable.',
    'tb.compress_level': 'Compression',
    'tb.compress_low':   'Low',
    'tb.compress_mid':   'Mid',
    'tb.compress_high':  'High',
    'tb.compress_quality': 'Quality',
    'tb.compress_quality_small':    'Smaller',
    'tb.compress_quality_balanced': 'Balanced',
    'tb.compress_quality_sharp':    'Sharp',
    'tb.text_color':     'Text color',
    'tb.custom_color':   'Custom color',
    'tb.zoom_in':        'Zoom in',
    'tb.zoom_out':       'Zoom out',
    'tb.reset_zoom':     'Reset zoom',
    'tb.snap_on':        'Snap on',
    'tb.snap_off':       'Snap off',
    'tb.snap_to_lines':  'Snap to detected form lines',
    'tb.font_family':    'Font family',
    'tb.font_size':      'Font size',
    'tb.lang':           'Language',
    'tb.theme':          'Theme',
    'theme.system':      'System',
    'theme.light':       'Light',
    'theme.dark':        'Dark',
    'theme.sepia':       'Sepia',
    'theme.solarized_light': 'Solarized Light',
    'theme.gruvbox_light': 'Gruvbox Light',
    'theme.catppuccin_latte': 'Catppuccin Latte',
    'theme.github_light': 'GitHub Light',
    'theme.hc':          'High Contrast',
    'theme.solarized':   'Solarized',
    'theme.dracula':     'Dracula',
    'theme.nord':        'Nord',
    'theme.gruvbox':     'Gruvbox',
    'theme.catppuccin':  'Catppuccin',
    'theme.tokyo':       'Tokyo Night',
    'wm.enabled':        'Add watermark to PDF',
    'wm.status_on':      'Added',
    'wm.status_off':     'Not added',
    'wm.text':           'Watermark text',
    'wm.placeholder':    'DRAFT',
    'wm.size':           'Size',
    'wm.color':          'Color',
    'wm.opacity':        'Opacity',
    'wm.rotation':       'Rotation',
    // Page numbering
    'pn.label':           'Page numbers',
    'pn.enabled':         'Insert page numbers',
    'pn.status_on':       'Added',
    'pn.status_off':      'Not added',
    'pn.format':          'Format',
    'pn.format_aria':     'Page number format',
    'pn.format_page_total': 'Page 1 of 5',
    'pn.format_page':     'Page 1',
    'pn.position':        'Position',
    'pn.position_aria':   'Page number position',
    'pn.bottom_center':   'Bottom center',
    'pn.bottom_right':    'Bottom right',
    'pn.bottom_left':     'Bottom left',
    'pn.top_center':      'Top center',
    'pn.top_right':       'Top right',
    'pn.top_left':        'Top left',
    'pn.start':           'Start',
    'pn.size':            'Size',
    'pn.margin':          'Margin',
    'pn.color':           'Color',
    'pn.color_value':     'Page number color {color}',
    // Floating toolbar
    'ft.color':           'Color',
    'ft.width':           'Width',
    'ft.opacity':         'Opacity',
    'ft.custom_color':    'Custom color',
    'ft.bold':            'Bold',
    'ft.italic':          'Italic',
    'ft.underline':       'Underline',
    'ft.delete':          'Delete',
    // Date format picker
    'date.system_default': 'System default',
    // Shared colour labels
    'color.black':        'Black',
    'color.blue':         'Blue',
    'color.red':          'Red',
    'color.green':        'Green',
    'color.yellow':       'Yellow',
    // Confirmations and alerts
    'confirm.clear_annotations': 'Remove all annotations?',
    'confirm.clear_recent_pdfs': 'Remove all cached recent PDFs?',
    'drop.not_pdf':       '{name} is {type} — only PDFs can be opened here.',
    'drop.file_type':     'a {type} file',
    'drop.this_file':     'this file',
    'err.merge_pdf':      'Could not merge PDF: {message}',
    'err.reorder_pages':  'Could not reorder pages: {message}',
    'err.rotate_page':    'Could not rotate page: {message}',
    'err.delete_page':    'Could not delete page: {message}',
    'err.pdf_loading':    'PDF is still loading; please wait and try again.',
    'err.redacted_pdf':   'Could not build redacted PDF: {message}',
    'err.compressed_pdf': 'Could not build compressed PDF: {message}',
    'err.build_pdf':      'Could not build PDF: {message}',
    'err.read_image':     'Could not read image: {message}',
    'img.err.empty':      'File is empty.',
    'img.err.too_large_mb': 'Image is too large ({mb} MB). Max 10 MB.',
    'img.err.unsupported_extension': 'Unsupported file extension (.{ext}). Allowed: png, jpg, jpeg, gif, webp.',
    'img.err.unsupported_type': 'Unsupported file type ({type}). Allowed: PNG, JPG, GIF, WebP.',
    'img.err.too_large_side': 'Image is too large to embed ({width}×{height}). Max {max}×{max} pixels per side.',
    'img.err.too_large_pixels': 'Image is too large to embed ({pixels} pixels). Max {max} MP.',
    // PDF viewer and app shell
    'pdf.error.title':    "Couldn't open this PDF",
    'pdf.error.password': 'Could not unlock this password-protected PDF.',
    'pdf.error.invalid':  "This file isn't a valid PDF — the contents look corrupt or it's not really a PDF.",
    'pdf.error.generic':  'Could not open this PDF.',
    'pdf.close':          'Close this PDF',
    'pdf.loading':        'Loading PDF…',
    'pdf.password.title': 'Unlock PDF',
    'pdf.password.description': 'Enter the password to open this PDF. The password stays in this session only.',
    'pdf.password.incorrect': 'That password did not work. Try again.',
    'pdf.password.label': 'Password',
    'pdf.password.unlock': 'Unlock',
    'pdf.password.cancel': 'Cancel',
    'pages.thumbnails':   'Page thumbnails',
    'app.error.title':    'Something went wrong.',
    'app.error.body':     'The app hit an unexpected error. Your most recent edits were auto-saved a moment ago and should reappear after reloading.',
    'app.error.reload':   'Reload',
    // Mode banner
    'mb.text.label':      'Add text',
    'mb.text.help':       'Click on a page to place a text box.',
    'mb.signature.label': 'Place signature',
    'mb.signature.help':  'Click on a page to drop your signature.',
    'mb.select.label':    'Select',
    'mb.select.help':     'Click an item to drag, resize, or delete.',
    'mb.draw.label':      'Draw',
    'mb.draw.help':       'Click and drag on a page to draw a stroke.',
    'mb.image.label':     'Place image',
    'mb.image.help':      "Click on a page to drop your image. Images are session-only — they aren't saved with the PDF.",
    'mb.edit.label':      'Edit text',
    'mb.edit.help':       'Click any word in the PDF to rewrite it in place.',
    'mb.redact.label':    'Redact',
    'mb.redact.help':     'Drag over sensitive content to cover it with an opaque block.',
    'mb.esc':             'Esc to exit',
    'mb.exit':            'Exit',
    // Recent files sidebar
    'rs.recent_files':    'Recent files',
    'rs.recent':          'Recent',
    'rs.open_new':        'Open new PDF',
    'rs.title_hover':     'Recent files (hover)',
    'rs.title_tap':       'Recent files (tap)',
    'rs.new_pdf':         'New PDF',
    'rs.no_cached':       'No cached files yet. Open one above to get started.',
    'rs.close':           'Close',
    'rs.remove':          'Remove from recent',
    // Signature modal
    'sm.title':            'Add signature',
    'sm.color':            'Color',
    'sm.color.black':      'Black',
    'sm.color.ink':        'Ink',
    'sm.color.blue':       'Blue',
    'sm.tab.draw':         'Draw',
    'sm.tab.type':         'Type',
    'sm.tab.generate':     'Generate',
    'sm.saved':            'Saved — click to use',
    'sm.delete_saved':     'Delete saved signature',
    'sm.draw.help':        'Sign with your mouse or finger.',
    'sm.draw.empty_alert': 'Please draw a signature first.',
    'sm.type.help':        'Type, then pick a handwriting style.',
    'sm.type.placeholder': 'Your name',
    'sm.type.empty_alert': 'Please type your name first.',
    'sm.generate.help':    'Type your name, then shuffle a generated signature.',
    'sm.generate.shuffle': 'Shuffle',
    'sm.generate.style':   'Style',
    'sm.generate.style.readable': 'Readable',
    'sm.generate.style.formal':  'Formal',
    'sm.generate.style.expressive': 'Expressive',
    'sm.generate.style.minimal': 'Minimal',
    'sm.generate.unsupported': 'Generated signatures support Latin letters only. Use Draw or Type for this name.',
    'sm.generate.legibility': 'Legibility',
    'sm.generate.flourish':   'Flourish',
    'sm.generate.preview':    'Generated signature preview',
    'sm.generate.empty_alert': 'Please type your name first.',
    'sm.clear':            'Clear',
    'sm.use':              'Use signature',
    'sm.signature_text':   'Signature text',
    // Profile dialog
    'pd.title':              'Profile',
    'pd.description':        'Saved values you can drop into forms. Click Insert, then click on a page to place. Stored only in your browser.',
    'pd.label_placeholder':  'Label',
    'pd.value_placeholder':  '(empty)',
    'pd.open_first':         'Open a PDF first',
    'pd.insert_into_click':  'Insert into the next click',
    'pd.insert':             'Insert',
    'pd.remove_field':       'Remove field',
    'pd.add_field':          'Add field',
    // Toolbar — snap label (in addition to snap_on/snap_off)
    'tb.snap':              'Snap',
    // Empty state
    'es.heading':        'Fill & sign a PDF',
    'es.description':    'Drop in a PDF and fill its fields, place text, drop a signature, draw freehand. Everything stays in your browser — nothing leaves your device.',
    'es.drag':           'Drag a PDF here',
    'es.or_click':       'or click anywhere to choose a file',
    'es.tap':           'Tap to choose a PDF',
    'ob.add_text.body_touch': 'Tap anywhere on the page to drop a text box. With snap on, the box auto-aligns to the form row you tapped. Pick font, size, and colour from the toolbar — those controls only appear in text mode. Tap outside the box when you\'re done.',
    'es.open_pdf':       'Open PDF',
    'es.try_sample':     'Try a sample form',
    'es.recent':         'Recent',
    'es.clear_recents':  'Clear all',
    // Pages drawer
    'pages.title':       'Pages',
    'pages.description': 'Jump between pages, rotate, delete, or reorder before downloading.',
    'pages.page':        'Page',
    'pages.view_page':   'View page',
    'pages.move_up':     'Move page up',
    'pages.move_down':   'Move page down',
    'pages.rotate_ccw':  'Rotate page counter-clockwise',
    'pages.rotate_cw':   'Rotate page clockwise',
    'pages.delete':      'Delete page',
    'pages.delete_title': 'Delete page {page}?',
    'pages.delete_confirm': 'This page will be removed from the PDF. This cannot be undone.',
    'pages.delete_cancel': 'Keep page',
    'pages.delete_action': 'Delete page',
    'pages.close':       'Close',
    'pages.apply':       'Apply order',
    // Onboarding
    'ob.welcome.title':  'Welcome to PDF Helper',
    'ob.welcome.body':   'Fill, sign, edit, merge, and reorder PDFs entirely in your browser. This guided tour walks through every tool — feel free to skip steps you already know.',
    'ob.privacy.title':  'Your files stay on your device',
    'ob.privacy.body':   'Nothing is ever uploaded — every operation runs locally. The PDF you open, the values you type, your saved profile and signatures all live only in this browser. Closing the tab clears the open file; the rest stays in your browser storage until you remove it.',
    'ob.add_text.title': 'Add text anywhere',
    'ob.add_text.body':  "Click anywhere on the page to drop a text box. With snap on, the box auto-aligns to the form row you clicked. Pick font, size, and colour from the toolbar — those controls only appear in text mode. Click outside the box when you're done.",
    'ob.snap.title':     'Snap to form fields',
    'ob.snap.body':      "Snap auto-detects form rows, vertical dividers, and signature underlines so your text lands exactly where it should. It's on by default. Toggle it off (the dashed icon next to the font size) for unusual layouts where you want to place text freely.",
    'ob.edit.title':     'Edit existing PDF text',
    'ob.edit.body':      "Click any word that's already on the page. A white cover masks the original glyphs and you type the replacement in the matching font, size, and colour. The editor wrapper drags freely; the cover stays anchored where the original text was, so the masking always lines up.",
    'ob.date.title':     "Insert today's date",
    'ob.date.body':      "Drops the current date as a text annotation. Format follows your selected language: DD.MM.YYYY for German, MM/DD/YYYY for US English, YYYY-MM-DD for ISO, etc. Switch the format from the floating toolbar that pops up after placing.",
    'ob.profile.title':  'Profile auto-fill',
    'ob.profile.body':   "Save common values once (name, address, phone, IBAN, anything you fill in often) and click them to insert anywhere on the page — no retyping. Profile lives in your browser's localStorage; never sent anywhere.",
    'ob.sign.title':     'Sign',
    'ob.sign.body':      'Draw your signature with mouse, trackpad, or touch. Save it for reuse — saved signatures appear in the picker every time you sign. Pick black, navy ink, royal blue, or any custom colour from the colour wheel. Resize after placing.',
    'ob.draw.title':     'Draw freehand',
    'ob.draw.body':      'Pen mode for free-hand strokes — perfect for circling, ticking, or arrows. Pick colour (preset or custom from the wheel), width, and opacity from the popover that appears next to the pen button. Strokes save as vectors in the output.',
    'ob.image.title':    'Add an image',
    'ob.image.body':     'Drop in a photo, scanned signature, or any image (PNG, JPEG, GIF, WebP). Up to 5000×5000 pixels and 20 megapixels. Resize and reposition after placing. Useful for adding an existing scanned signature to a form.',
    'ob.select.title':   'Select & rearrange',
    'ob.select.body':    'Click any annotation to select it. Drag to move, grab the corners to resize, or press Backspace/Delete to remove. Cmd/Ctrl+Z undoes (50 levels of history); Cmd/Ctrl+Shift+Z redoes. The Clear All button wipes annotations only — the underlying PDF stays intact.',
    'ob.merge.title':    'Merge another PDF',
    'ob.merge.body':     "Add pages from a second PDF to the start or end of this one — useful for prepending a cover letter or appending receipts. Existing annotations stay anchored to their original pages: a signature you'd dropped on page 2 stays on what's now page 2 (or 3, after a prepend).",
    'ob.reorder.title':  'Reorder pages',
    'ob.reorder.body':   'Drag any thumbnail in the right rail to a new position to reorder pages. The PDF re-saves and your annotations follow them automatically. The rail only shows on wider screens (1024px+) — rotate to landscape on a tablet to see it.',
    'ob.undo.title':     'Undo & Clear',
    'ob.undo.body':      'Undo backs out your last action; 50 levels of history are kept. Cmd/Ctrl+Z works too. Clear All wipes every annotation on the current PDF in one go (with a confirmation prompt). Both leave the underlying PDF untouched.',
    'ob.zoom.title':     'Zoom',
    'ob.zoom.body':      "Zoom in/out from the toolbar for tight spots — useful when a form's fields are dense or when placing a signature precisely. Click the percentage between the +/− buttons to reset to 100%.",
    'ob.lang.title':     'Language',
    'ob.lang.body':      'Seventeen UI languages: English, German, Spanish, French, Chinese, Japanese, Arabic (with right-to-left layout), Hindi, Bengali, Russian, Portuguese, Indonesian, Turkish, Vietnamese, Thai, Korean, and Italian. Auto-detected from your browser; unsupported locales default to English. Switch anytime here.',
    'ob.theme.title':    'Theme',
    'ob.theme.body':     'Fifteen themes: System, Light, Dark, Sepia, High Contrast, popular light palettes, and editor classics like Solarized, Dracula, Nord, Gruvbox, Catppuccin, and Tokyo Night. Choice persists across visits.',
    'ob.download.title': 'Download',
    'ob.download.body':  "Save your filled PDF. Click the chevron next to Download for compression options — 'Make smaller' rasterises pages to JPEG (96/150/200 DPI presets) for big size reductions on scanned content. Tricky source files automatically fall back to a raster export so you still get a usable file.",
    'ob.skip':           'Skip',
    'ob.next':           'Next',
    'ob.back':           'Back',
    'ob.done':           'Done',
  },
  de: {
    'tb.open':           'PDF öffnen',
    'tb.pages':          'Seiten',
    'tb.help':           'Hilfe',
    'tb.merge_pdf':      'Weitere PDF zusammenführen',
    'tb.merge_at_start': 'Am Anfang einfügen',
    'tb.merge_at_end':   'Am Ende einfügen',
    'tb.add_text':       'Text einfügen',
    'tb.edit_text':      'Text bearbeiten',
    'tb.add_signature':  'Unterschrift einfügen',
    'tb.add_image':      'Bild einfügen (nur Sitzung)',
    'tb.watermark':      'Wasserzeichen',
    'tb.draw':           'Zeichnen',
    'tb.select':         'Auswählen',
    'tb.pen_settings':   'Stift-Einstellungen',
    'draw.tool':         'Werkzeug',
    'draw.tool.pen':     'Stift',
    'draw.tool.marker':  'Marker',
    'draw.tool.highlighter': 'Textmarker',
    'draw.tool.line':    'Linie',
    'draw.tool.arrow':   'Pfeil',
    'draw.tool.eraser':  'Radierer',
    'draw.presets':      'Vorgaben',
    'draw.preset.pen':   'Stift',
    'draw.preset.red_pen': 'Roter Stift',
    'draw.preset.marker': 'Marker',
    'draw.preset.highlighter': 'Textmarker',
    'draw.undo_stroke':  'Strich rückgängig',
    'tb.insert_date':    'Heutiges Datum einfügen',
    'tb.profile':        'Profil',
    'tb.undo':           'Rückgängig',
    'tb.clear_all':      'Alle löschen',
    'tb.download':       'Herunterladen',
    'tb.download_options': 'Download-Optionen',
    'tb.compress_pdf':   'PDF verkleinern',
    'tb.compress_hint':  'Seiten als JPEG rastern. Kleinere Datei, aber Text ist nicht mehr auswählbar.',
    'tb.compress_level': 'Komprimierung',
    'tb.compress_low':   'Niedrig',
    'tb.compress_mid':   'Mittel',
    'tb.compress_high':  'Hoch',
    'tb.compress_quality': 'Qualität',
    'tb.compress_quality_small':    'Kleiner',
    'tb.compress_quality_balanced': 'Ausgewogen',
    'tb.compress_quality_sharp':    'Scharf',
    'tb.text_color':     'Textfarbe',
    'tb.custom_color':   'Eigene Farbe',
    'tb.zoom_in':        'Vergrößern',
    'tb.zoom_out':       'Verkleinern',
    'tb.reset_zoom':     'Zoom zurücksetzen',
    'tb.snap_on':        'Einrasten an',
    'tb.snap_off':       'Einrasten aus',
    'tb.snap_to_lines':  'An erkannten Formularlinien einrasten',
    'tb.font_family':    'Schriftart',
    'tb.font_size':      'Schriftgröße',
    'tb.lang':           'Sprache',
    'tb.theme':          'Design',
    'theme.system':      'System',
    'theme.light':       'Hell',
    'theme.dark':        'Dunkel',
    'theme.sepia':       'Sepia',
    'theme.solarized_light': 'Solarized Light',
    'theme.gruvbox_light': 'Gruvbox Light',
    'theme.catppuccin_latte': 'Catppuccin Latte',
    'theme.github_light': 'GitHub Light',
    'theme.hc':          'Hoher Kontrast',
    'theme.solarized':   'Solarized',
    'theme.dracula':     'Dracula',
    'theme.nord':        'Nord',
    'theme.gruvbox':     'Gruvbox',
    'theme.catppuccin':  'Catppuccin',
    'theme.tokyo':       'Tokyo Night',
    'wm.enabled':        'Wasserzeichen zum PDF hinzufügen',
    'wm.status_on':      'Hinzugefügt',
    'wm.status_off':     'Nicht hinzugefügt',
    'wm.text':           'Wasserzeichen-Text',
    'wm.placeholder':    'ENTWURF',
    'wm.size':           'Größe',
    'wm.color':          'Farbe',
    'wm.opacity':        'Deckkraft',
    'wm.rotation':       'Drehung',
    'mb.text.label':      'Text einfügen',
    'mb.text.help':       'Klicken Sie auf eine Seite, um ein Textfeld zu platzieren.',
    'mb.signature.label': 'Unterschrift platzieren',
    'mb.signature.help':  'Klicken Sie auf eine Seite, um Ihre Unterschrift abzulegen.',
    'mb.select.label':    'Auswählen',
    'mb.select.help':     'Klicken Sie ein Element an, um es zu ziehen, zu skalieren oder zu löschen.',
    'mb.draw.label':      'Zeichnen',
    'mb.draw.help':       'Klicken und ziehen Sie auf einer Seite, um einen Strich zu zeichnen.',
    'mb.image.label':     'Bild platzieren',
    'mb.image.help':      'Klicken Sie auf eine Seite, um Ihr Bild abzulegen. Bilder sind nur für diese Sitzung — sie werden nicht mit dem PDF gespeichert.',
    'mb.edit.label':      'Text bearbeiten',
    'mb.edit.help':       'Klicken Sie ein beliebiges Wort im PDF an, um es zu ersetzen.',
    'mb.esc':             'Esc zum Beenden',
    'mb.exit':            'Beenden',
    'rs.recent_files':    'Zuletzt verwendete Dateien',
    'rs.recent':          'Zuletzt verwendet',
    'rs.open_new':        'Neue PDF öffnen',
    'rs.title_hover':     'Zuletzt verwendete Dateien (Hover)',
    'rs.title_tap':       'Zuletzt verwendete Dateien (Tippen)',
    'rs.new_pdf':         'Neue PDF',
    'rs.no_cached':       'Noch keine zwischengespeicherten Dateien. Oben eine öffnen.',
    'rs.close':           'Schließen',
    'rs.remove':          'Aus Verlauf entfernen',
    'sm.title':            'Unterschrift hinzufügen',
    'sm.color':            'Farbe',
    'sm.color.black':      'Schwarz',
    'sm.color.blue':       'Blau',
    'sm.tab.draw':         'Zeichnen',
    'sm.tab.type':         'Tippen',
    'sm.saved':            'Gespeichert — zum Verwenden klicken',
    'sm.delete_saved':     'Gespeicherte Unterschrift löschen',
    'sm.draw.help':        'Unterschreibe mit Maus oder Finger.',
    'sm.draw.empty_alert': 'Bitte zuerst eine Unterschrift zeichnen.',
    'sm.type.help':        'Tippe ein und wähle dann einen Handschrift-Stil.',
    'sm.type.placeholder': 'Dein Name',
    'sm.type.empty_alert': 'Bitte zuerst deinen Namen eingeben.',
    'sm.clear':            'Löschen',
    'sm.use':              'Unterschrift verwenden',
    'sm.signature_text':   'Unterschrifttext',
    'pd.title':              'Profil',
    'pd.description':        'Gespeicherte Werte zum Einfügen in Formulare. Klicke Einfügen und dann auf eine Seite. Wird nur in deinem Browser gespeichert.',
    'pd.label_placeholder':  'Label',
    'pd.value_placeholder':  '(leer)',
    'pd.open_first':         'Erst eine PDF öffnen',
    'pd.insert_into_click':  'In den nächsten Klick einfügen',
    'pd.insert':             'Einfügen',
    'pd.remove_field':       'Feld entfernen',
    'pd.add_field':          'Feld hinzufügen',
    'tb.snap':              'Einrasten',
    'es.heading':        'PDF ausfüllen & unterschreiben',
    'es.description':    'Lege eine PDF ab, fülle ihre Felder aus, setze Text, eine Unterschrift, oder zeichne freihändig. Alles bleibt in deinem Browser — nichts verlässt dein Gerät.',
    'es.drag':           'PDF hierher ziehen',
    'es.or_click':       'oder klicke irgendwo, um eine Datei zu wählen',
    'es.tap':           'Tippe, um eine PDF auszuwählen',
    'ob.add_text.body_touch': 'Tippe irgendwo auf die Seite, um ein Textfeld zu setzen. Mit aktivem Einrasten richtet sich das Feld automatisch an der erkannten Formularzeile aus. Schriftart, Größe und Farbe wählst du in der Symbolleiste — diese Bedienelemente erscheinen nur im Textmodus. Tippe außerhalb, um fertig zu sein.',
    'es.open_pdf':       'PDF öffnen',
    'es.try_sample':     'Beispielformular ausprobieren',
    'es.recent':         'Zuletzt verwendet',
    'es.clear_recents':  'Alle entfernen',
    'pages.title':       'Seiten',
    'pages.description': 'Zwischen Seiten wechseln, sie drehen, löschen oder vor dem Download neu anordnen.',
    'pages.page':        'Seite',
    'pages.view_page':   'Seite anzeigen',
    'pages.move_up':     'Seite nach oben',
    'pages.move_down':   'Seite nach unten',
    'pages.rotate_ccw':  'Seite gegen den Uhrzeigersinn drehen',
    'pages.rotate_cw':   'Seite im Uhrzeigersinn drehen',
    'pages.delete':      'Seite löschen',
    'pages.delete_title': 'Seite {page} löschen?',
    'pages.delete_confirm': 'Diese Seite wird aus der PDF entfernt. Das kann nicht rückgängig gemacht werden.',
    'pages.delete_cancel': 'Seite behalten',
    'pages.delete_action': 'Seite löschen',
    'pages.close':       'Schließen',
    'pages.apply':       'Reihenfolge übernehmen',
    'ob.welcome.title':  'Willkommen bei PDF Helper',
    'ob.welcome.body':   'PDFs direkt im Browser ausfüllen, unterschreiben, bearbeiten, zusammenführen und neu anordnen. Diese geführte Tour stellt jedes Werkzeug vor — Schritte, die du schon kennst, kannst du überspringen.',
    'ob.privacy.title':  'Deine Dateien bleiben auf deinem Gerät',
    'ob.privacy.body':   'Nichts wird hochgeladen — alles passiert lokal. Die geöffnete PDF, deine eingegebenen Werte, dein Profil und gespeicherte Unterschriften bleiben nur in diesem Browser. Beim Schließen des Tabs verschwindet die Datei; der Rest bleibt im Browser-Speicher, bis du ihn entfernst.',
    'ob.add_text.title': 'Text überall einfügen',
    'ob.add_text.body':  'Klicke irgendwo auf die Seite, um ein Textfeld zu setzen. Mit aktivem Einrasten richtet sich das Feld automatisch an der erkannten Formularzeile aus. Schriftart, Größe und Farbe wählst du in der Symbolleiste — diese Bedienelemente erscheinen nur im Textmodus. Klicke außerhalb, um fertig zu sein.',
    'ob.snap.title':     'An Formularfeldern einrasten',
    'ob.snap.body':      'Das Einrasten erkennt Formularzeilen, Trennstriche und Unterschriftslinien automatisch und platziert deinen Text genau richtig. Standardmäßig aktiv. Schalte es aus (das gestrichelte Symbol neben der Schriftgröße), wenn du frei platzieren möchtest.',
    'ob.edit.title':     'Vorhandenen PDF-Text bearbeiten',
    'ob.edit.body':      'Klicke ein beliebiges Wort auf der Seite. Eine weiße Fläche überdeckt das Original und du tippst den Ersatz in passender Schrift, Größe und Farbe. Der Editor lässt sich frei verschieben; die Abdeckung bleibt am Originaltext verankert, sodass die Maskierung immer passt.',
    'ob.date.title':     'Heutiges Datum einfügen',
    'ob.date.body':      'Setzt das aktuelle Datum als Textannotation. Das Format folgt deiner Sprache: TT.MM.JJJJ für Deutsch, MM/TT/JJJJ für US-Englisch, JJJJ-MM-TT für ISO usw. In der Schwebe-Symbolleiste nach dem Platzieren kannst du das Format wechseln.',
    'ob.profile.title':  'Profil-Autoausfüllen',
    'ob.profile.body':   'Speichere häufige Werte einmal (Name, Adresse, Telefon, IBAN — alles, was du oft eingibst) und füge sie per Klick auf der Seite ein, ohne sie erneut zu tippen. Das Profil liegt im localStorage des Browsers und wird nirgendwohin gesendet.',
    'ob.sign.title':     'Unterschreiben',
    'ob.sign.body':      'Zeichne deine Unterschrift mit Maus, Trackpad oder Finger. Speichere sie zur Wiederverwendung — gespeicherte Unterschriften erscheinen jedes Mal im Auswahldialog. Schwarz, Tinten-Marineblau, Königsblau oder eine beliebige Farbe aus dem Farbrad. Nach dem Platzieren ist die Größe anpassbar.',
    'ob.draw.title':     'Frei zeichnen',
    'ob.draw.body':      'Stiftmodus für freie Striche — perfekt für Kreise, Häkchen oder Pfeile. Farbe (Vorgaben oder benutzerdefiniert aus dem Farbrad), Strichbreite und Deckkraft wählst du im Popup neben dem Stift-Button. Striche werden im Ergebnis als Vektoren gespeichert.',
    'ob.image.title':    'Bild einfügen',
    'ob.image.body':     'Füge ein Foto, einen Scan oder ein beliebiges Bild ein (PNG, JPEG, GIF, WebP). Bis zu 5000×5000 Pixel und 20 Megapixel. Nach dem Platzieren skalier- und verschiebbar. Praktisch, um eine eingescannte Unterschrift in ein Formular einzufügen.',
    'ob.select.title':   'Auswählen & anordnen',
    'ob.select.body':    'Klicke eine Annotation an, um sie auszuwählen. Verschiebe sie per Drag, fasse die Ecken zum Skalieren oder drücke Rücktaste/Entf zum Löschen. Cmd/Strg+Z macht rückgängig (50 Stufen Verlauf); Cmd/Strg+Umschalt+Z stellt wieder her. „Alle löschen" entfernt nur Annotationen — die PDF selbst bleibt unangetastet.',
    'ob.merge.title':    'Weitere PDF zusammenführen',
    'ob.merge.body':     'Hänge Seiten einer zweiten PDF am Anfang oder Ende dieser PDF an — praktisch für ein Anschreiben oder Belege. Vorhandene Annotationen bleiben an ihrer ursprünglichen Seite verankert: eine Unterschrift auf Seite 2 bleibt auf der jetzt-Seite-2 (oder 3 nach einem Voranstellen).',
    'ob.reorder.title':  'Seiten neu anordnen',
    'ob.reorder.body':   'Ziehe ein Vorschaubild in der rechten Leiste an eine neue Position, um die Reihenfolge zu ändern. Die PDF wird neu gespeichert, deine Annotationen folgen automatisch. Die Leiste erscheint nur auf breiteren Bildschirmen (1024 px+) — auf einem Tablet ins Querformat drehen.',
    'ob.undo.title':     'Rückgängig & Alle löschen',
    'ob.undo.body':      'Macht die letzte Aktion rückgängig; 50 Verlaufsstufen werden gespeichert. Cmd/Strg+Z funktioniert auch. „Alle löschen" entfernt jede Annotation der aktuellen PDF auf einmal (mit Sicherheitsabfrage). Beides lässt die PDF selbst unverändert.',
    'ob.zoom.title':     'Zoom',
    'ob.zoom.body':      'Vergrößern/verkleinern aus der Symbolleiste — nützlich bei dichten Formularen oder beim präzisen Platzieren einer Unterschrift. Klicke auf die Prozentangabe zwischen +/−, um auf 100 % zurückzusetzen.',
    'ob.lang.title':     'Sprache',
    'ob.lang.body':      'Siebzehn Sprachen: Englisch, Deutsch, Spanisch, Französisch, Chinesisch, Japanisch, Arabisch (mit Rechts-nach-Links-Layout), Hindi, Bengali, Russisch, Portugiesisch, Indonesisch, Türkisch, Vietnamesisch, Thai, Koreanisch und Italienisch. Wird aus deinem Browser erkannt; nicht unterstützte Gebietsschemas fallen auf Englisch zurück. Jederzeit hier umschaltbar.',
    'ob.theme.title':    'Design',
    'ob.theme.body':     'Fünfzehn Designs: System, Hell, Dunkel, Sepia, Hoher Kontrast, beliebte helle Paletten und Editor-Klassiker wie Solarized, Dracula, Nord, Gruvbox, Catppuccin und Tokyo Night. Die Auswahl bleibt über Besuche hinweg erhalten.',
    'ob.download.title': 'Herunterladen',
    'ob.download.body':  'Speichere deine ausgefüllte PDF. Klicke auf den Pfeil neben „Herunterladen" für Komprimierungsoptionen — „Verkleinern" rastert die Seiten als JPEG (Voreinstellungen 96/150/200 DPI) und reduziert die Dateigröße bei gescannten Inhalten deutlich. Schwierige Quelldateien fallen automatisch auf Raster-Export zurück, damit du trotzdem ein nutzbares Ergebnis bekommst.',
    'ob.skip':           'Überspringen',
    'ob.next':           'Weiter',
    'ob.back':           'Zurück',
    'ob.done':           'Fertig',
  },
  fr: {
    'tb.open':           'Ouvrir un PDF',
    'tb.merge_pdf':      'Fusionner un autre PDF',
    'tb.merge_at_start': 'Ajouter au début',
    'tb.merge_at_end':   'Ajouter à la fin',
    'tb.add_text':       'Ajouter du texte',
    'tb.edit_text':      'Modifier le texte',
    'tb.add_signature':  'Ajouter une signature',
    'tb.add_image':      'Ajouter une image (session uniquement)',
    'tb.draw':           'Dessiner',
    'tb.select':         'Sélectionner',
    'tb.pen_settings':   'Réglages du stylo',
    'tb.insert_date':    "Insérer la date d'aujourd'hui",
    'tb.profile':        'Profil',
    'tb.undo':           'Annuler',
    'tb.clear_all':      'Tout effacer',
    'tb.download':       'Télécharger',
    'tb.zoom_in':        'Zoom avant',
    'tb.zoom_out':       'Zoom arrière',
    'tb.reset_zoom':     'Réinitialiser le zoom',
    'tb.snap_on':        'Aimantation activée',
    'tb.snap_off':       'Aimantation désactivée',
    'tb.snap_to_lines':  'Aimanter aux lignes du formulaire',
    'tb.font_family':    'Police',
    'tb.font_size':      'Taille de police',
    'tb.lang':           'Langue',
    'tb.theme':          'Thème',
    'theme.system':      'Système',
    'theme.light':       'Clair',
    'theme.dark':        'Sombre',
    'theme.sepia':       'Sépia',
    'theme.hc':          'Contraste élevé',
    'theme.solarized':   'Solarized',
    'theme.dracula':     'Dracula',
    'mb.text.label':      'Ajouter du texte',
    'mb.text.help':       'Cliquez sur une page pour placer une zone de texte.',
    'mb.signature.label': 'Placer la signature',
    'mb.signature.help':  'Cliquez sur une page pour déposer votre signature.',
    'mb.select.label':    'Sélectionner',
    'mb.select.help':     'Cliquez sur un élément pour le déplacer, redimensionner ou supprimer.',
    'mb.draw.label':      'Dessiner',
    'mb.draw.help':       'Cliquez et faites glisser sur une page pour tracer un trait.',
    'mb.image.label':     'Placer une image',
    'mb.image.help':      "Cliquez sur une page pour déposer votre image. Les images sont uniquement pour la session — elles ne sont pas enregistrées avec le PDF.",
    'mb.edit.label':      'Modifier le texte',
    'mb.edit.help':       "Cliquez sur n'importe quel mot dans le PDF pour le réécrire en place.",
    'mb.esc':             'Échap pour quitter',
    'mb.exit':            'Quitter',
    'es.heading':        'Remplir & signer un PDF',
    'es.description':    'Déposez un PDF, remplissez ses champs, ajoutez du texte, signez ou dessinez à main levée. Tout reste dans votre navigateur — rien ne quitte votre appareil.',
    'es.drag':           'Glissez un PDF ici',
    'es.or_click':       'ou cliquez n\'importe où pour choisir un fichier',
    'es.tap':           'Touchez pour choisir un PDF',
    'ob.add_text.body_touch': 'Touchez n\'importe où sur la page pour déposer une zone de texte. Le mode aimantation l\'aligne automatiquement sur les lignes du formulaire.',
    'es.open_pdf':       'Ouvrir un PDF',
    'es.try_sample':     'Essayer un formulaire d\'exemple',
    'es.recent':         'Récents',
    'es.clear_recents':  'Tout effacer',
    'ob.welcome.title':  'Bienvenue sur PDF Helper',
    'ob.welcome.body':   'Remplissez, signez, dessinez et modifiez des PDF entièrement dans votre navigateur. Rien ne quitte votre appareil. Choisissez un formulaire d\'exemple ci-dessous ou ouvrez le vôtre pour commencer.',
    'ob.add_text.title': 'Ajouter du texte n\'importe où',
    'ob.add_text.body':  'Cliquez n\'importe où sur la page pour déposer une zone de texte. Le mode aimantation l\'aligne automatiquement sur les lignes du formulaire.',
    'ob.edit.title':     'Modifier le texte existant',
    'ob.edit.body':      'Cliquez sur n\'importe quel mot du PDF pour le réécrire en place. Gras, couleur, alignement et mise en page multi-ligne sont préservés.',
    'ob.sign.title':     'Signer',
    'ob.sign.body':      'Dessinez ou tapez votre signature, puis cliquez sur la page pour la placer.',
    'ob.download.title': 'Télécharger',
    'ob.download.body':  'Enregistrez le PDF avec toutes vos modifications. Les fichiers sources difficiles basculent sur un export raster pour obtenir tout de même un résultat.',
    'ob.skip':           'Ignorer',
    'ob.next':           'Suivant',
    'ob.done':           'Terminé',
  },
  hi: {
    'tb.open':           'PDF खोलें',
    'tb.merge_pdf':      'दूसरा PDF मर्ज करें',
    'tb.merge_at_start': 'शुरुआत में जोड़ें',
    'tb.merge_at_end':   'अंत में जोड़ें',
    'tb.add_text':       'टेक्स्ट जोड़ें',
    'tb.edit_text':      'टेक्स्ट संपादित करें',
    'tb.add_signature':  'हस्ताक्षर जोड़ें',
    'tb.add_image':      'छवि जोड़ें (केवल इस सत्र में)',
    'tb.draw':           'चित्र बनाएँ',
    'tb.select':         'चुनें',
    'tb.pen_settings':   'पेन सेटिंग्स',
    'tb.insert_date':    'आज की तारीख डालें',
    'tb.profile':        'प्रोफ़ाइल',
    'tb.undo':           'पूर्ववत्',
    'tb.clear_all':      'सब साफ़ करें',
    'tb.download':       'डाउनलोड',
    'tb.zoom_in':        'ज़ूम इन',
    'tb.zoom_out':       'ज़ूम आउट',
    'tb.reset_zoom':     'ज़ूम रीसेट करें',
    'tb.snap_on':        'स्नैप चालू',
    'tb.snap_off':       'स्नैप बंद',
    'tb.snap_to_lines':  'पाई गई फ़ॉर्म लाइनों पर स्नैप करें',
    'tb.font_family':    'फ़ॉन्ट',
    'tb.font_size':      'फ़ॉन्ट आकार',
    'tb.lang':           'भाषा',
    'tb.theme':          'थीम',
    'es.heading':        'PDF भरें और हस्ताक्षर करें',
    'es.description':    'PDF छोड़ें और उसके फ़ील्ड भरें, टेक्स्ट रखें, हस्ताक्षर डालें, या मुक्त रूप से चित्र बनाएँ। सब कुछ आपके ब्राउज़र में रहता है — कुछ भी आपके डिवाइस से बाहर नहीं जाता।',
    'es.drag':           'PDF यहाँ खींचें',
    'es.or_click':       'या फ़ाइल चुनने के लिए कहीं भी क्लिक करें',
    'es.tap':           'PDF चुनने के लिए टैप करें',
    'ob.add_text.body_touch': 'टेक्स्ट बॉक्स रखने के लिए पृष्ठ पर कहीं भी टैप करें। स्नैप मोड इसे पाई गई फ़ॉर्म पंक्तियों से स्वचालित रूप से संरेखित करता है।',
    'es.open_pdf':       'PDF खोलें',
    'es.try_sample':     'नमूना फ़ॉर्म आज़माएँ',
    'es.recent':         'हाल ही में',
    'es.clear_recents':  'सब साफ़ करें',
    'ob.welcome.title':  'PDF Helper में आपका स्वागत है',
    'ob.welcome.body':   'पूरी तरह से अपने ब्राउज़र में PDF भरें, हस्ताक्षर करें, चित्र बनाएँ और संपादित करें। कुछ भी आपके डिवाइस से बाहर नहीं जाता। शुरू करने के लिए नीचे एक नमूना फ़ॉर्म चुनें या अपना खुद का खोलें।',
    'ob.add_text.title': 'कहीं भी टेक्स्ट जोड़ें',
    'ob.add_text.body':  'टेक्स्ट बॉक्स रखने के लिए पृष्ठ पर कहीं भी क्लिक करें। स्नैप मोड इसे पाई गई फ़ॉर्म पंक्तियों से स्वचालित रूप से संरेखित करता है।',
    'ob.edit.title':     'मौजूदा PDF टेक्स्ट संपादित करें',
    'ob.edit.body':      'PDF में किसी भी शब्द को क्लिक करें और उसे जगह पर ही बदलें। बोल्ड, रंग, संरेखण और बहु-पंक्ति लेआउट सुरक्षित रहते हैं।',
    'ob.sign.title':     'हस्ताक्षर',
    'ob.sign.body':      'अपना हस्ताक्षर बनाएँ या टाइप करें, फिर उसे रखने के लिए पृष्ठ पर क्लिक करें।',
    'ob.download.title': 'डाउनलोड',
    'ob.download.body':  'अपने सभी संपादनों के साथ PDF सहेजें। जटिल स्रोत फ़ाइलें रास्टर निर्यात पर वापस आ जाती हैं ताकि आप परिणाम प्राप्त कर सकें।',
    'ob.skip':           'छोड़ें',
    'ob.next':           'अगला',
    'ob.done':           'पूर्ण',
  },
  ar: {
    'tb.open':           'فتح PDF',
    'tb.merge_pdf':      'دمج ملف PDF آخر',
    'tb.merge_at_start': 'إضافة في البداية',
    'tb.merge_at_end':   'إضافة في النهاية',
    'tb.add_text':       'إضافة نص',
    'tb.edit_text':      'تعديل النص',
    'tb.add_signature':  'إضافة توقيع',
    'tb.add_image':      'إضافة صورة (الجلسة الحالية فقط)',
    'tb.draw':           'رسم',
    'tb.select':         'تحديد',
    'tb.pen_settings':   'إعدادات القلم',
    'tb.insert_date':    'إدراج تاريخ اليوم',
    'tb.profile':        'الملف الشخصي',
    'tb.undo':           'تراجع',
    'tb.clear_all':      'مسح الكل',
    'tb.download':       'تنزيل',
    'tb.zoom_in':        'تكبير',
    'tb.zoom_out':       'تصغير',
    'tb.reset_zoom':     'إعادة تعيين التكبير',
    'tb.snap_on':        'الالتقاط مفعّل',
    'tb.snap_off':       'الالتقاط معطّل',
    'tb.snap_to_lines':  'الالتقاط إلى خطوط النموذج المكتشفة',
    'tb.font_family':    'الخط',
    'tb.font_size':      'حجم الخط',
    'tb.lang':           'اللغة',
    'tb.theme':          'السمة',
    'es.heading':        'تعبئة وتوقيع PDF',
    'es.description':    'أفلت ملف PDF واملأ حقوله، ضع نصًا، أضف توقيعًا، أو ارسم بحرية. يبقى كل شيء داخل متصفحك — لا شيء يغادر جهازك.',
    'es.drag':           'اسحب ملف PDF إلى هنا',
    'es.or_click':       'أو انقر في أي مكان لاختيار ملف',
    'es.tap':           'اضغط لاختيار ملف PDF',
    'ob.add_text.body_touch': 'اضغط في أي مكان من الصفحة لإفلات مربع نص. يقوم وضع الالتقاط بمحاذاته تلقائيًا مع صفوف النموذج المكتشفة.',
    'es.open_pdf':       'فتح PDF',
    'es.try_sample':     'جرب نموذجًا تجريبيًا',
    'es.recent':         'الملفات الأخيرة',
    'es.clear_recents':  'مسح الكل',
    'ob.welcome.title':  'مرحبًا بك في PDF Helper',
    'ob.welcome.body':   'املأ ملفات PDF ووقّعها وارسم وعدّلها بالكامل في متصفحك. لا شيء يغادر جهازك. اختر نموذجًا تجريبيًا أدناه أو افتح ملفك الخاص للبدء.',
    'ob.add_text.title': 'أضف نصًا في أي مكان',
    'ob.add_text.body':  'انقر في أي مكان من الصفحة لإفلات مربع نص. يقوم وضع الالتقاط بمحاذاته تلقائيًا مع صفوف النموذج المكتشفة.',
    'ob.edit.title':     'تعديل نص PDF الحالي',
    'ob.edit.body':      'انقر على أي كلمة في PDF لإعادة كتابتها في مكانها. يتم الحفاظ على التنسيق الغامق واللون والمحاذاة وتنسيق الأسطر المتعددة.',
    'ob.sign.title':     'التوقيع',
    'ob.sign.body':      'ارسم توقيعك أو اكتبه، ثم انقر على الصفحة لوضعه.',
    'ob.download.title': 'تنزيل',
    'ob.download.body':  'احفظ ملف PDF مع جميع تعديلاتك. الملفات المعقدة ترجع إلى تصدير نقطي حتى تحصل على النتيجة.',
    'ob.skip':           'تخطي',
    'ob.next':           'التالي',
    'ob.done':           'تم',
  },
  bn: {
    'tb.open':           'PDF খুলুন',
    'tb.merge_pdf':      'আরেকটি PDF মার্জ করুন',
    'tb.merge_at_start': 'শুরুতে যোগ করুন',
    'tb.merge_at_end':   'শেষে যোগ করুন',
    'tb.add_text':       'টেক্সট যোগ করুন',
    'tb.edit_text':      'টেক্সট সম্পাদনা করুন',
    'tb.add_signature':  'স্বাক্ষর যোগ করুন',
    'tb.add_image':      'ছবি যোগ করুন (শুধু এই সেশন)',
    'tb.draw':           'আঁকুন',
    'tb.select':         'নির্বাচন করুন',
    'tb.pen_settings':   'কলমের সেটিংস',
    'tb.insert_date':    'আজকের তারিখ ঢোকান',
    'tb.profile':        'প্রোফাইল',
    'tb.undo':           'পূর্বাবস্থায় ফিরুন',
    'tb.clear_all':      'সব মুছুন',
    'tb.download':       'ডাউনলোড',
    'tb.zoom_in':        'বড় করুন',
    'tb.zoom_out':       'ছোট করুন',
    'tb.reset_zoom':     'জুম রিসেট করুন',
    'tb.snap_on':        'স্ন্যাপ চালু',
    'tb.snap_off':       'স্ন্যাপ বন্ধ',
    'tb.snap_to_lines':  'শনাক্ত করা ফর্ম লাইনে স্ন্যাপ করুন',
    'tb.font_family':    'ফন্ট',
    'tb.font_size':      'ফন্টের আকার',
    'tb.lang':           'ভাষা',
    'tb.theme':          'থিম',
    'es.heading':        'PDF পূরণ ও সাইন করুন',
    'es.description':    'একটি PDF ফেলে তার ক্ষেত্র পূরণ করুন, টেক্সট রাখুন, স্বাক্ষর ফেলুন, বা মুক্তভাবে আঁকুন। সব কিছু আপনার ব্রাউজারে থাকে — কিছুই আপনার ডিভাইস ছেড়ে যায় না।',
    'es.drag':           'PDF এখানে টেনে আনুন',
    'es.or_click':       'বা ফাইল বাছতে যেকোনো জায়গায় ক্লিক করুন',
    'es.tap':           'PDF বাছতে ট্যাপ করুন',
    'ob.add_text.body_touch': 'একটি টেক্সট বক্স ফেলতে পৃষ্ঠার যেকোনো জায়গায় ট্যাপ করুন। স্ন্যাপ মোড স্বয়ংক্রিয়ভাবে এটিকে শনাক্ত করা ফর্ম সারিগুলির সঙ্গে সংরেখণ করে।',
    'es.open_pdf':       'PDF খুলুন',
    'es.try_sample':     'নমুনা ফর্ম চেষ্টা করুন',
    'es.recent':         'সাম্প্রতিক',
    'es.clear_recents':  'সব মুছুন',
    'ob.welcome.title':  'PDF Helper-এ স্বাগতম',
    'ob.welcome.body':   'সম্পূর্ণরূপে আপনার ব্রাউজারে PDF পূরণ করুন, সাইন করুন, আঁকুন এবং সম্পাদনা করুন। কিছুই আপনার ডিভাইস ছেড়ে যায় না। শুরু করতে নিচে একটি নমুনা ফর্ম বেছে নিন বা নিজের একটি খুলুন।',
    'ob.add_text.title': 'যেকোনো জায়গায় টেক্সট যোগ করুন',
    'ob.add_text.body':  'একটি টেক্সট বক্স ফেলতে পৃষ্ঠার যেকোনো জায়গায় ক্লিক করুন। স্ন্যাপ মোড স্বয়ংক্রিয়ভাবে এটিকে শনাক্ত করা ফর্ম সারিগুলির সঙ্গে সংরেখণ করে।',
    'ob.edit.title':     'বিদ্যমান PDF টেক্সট সম্পাদনা করুন',
    'ob.edit.body':      'PDF-এর যেকোনো শব্দে ক্লিক করে সেখানেই পুনরায় লিখুন। বোল্ড, রঙ, সংরেখণ এবং বহু-লাইন বিন্যাস সংরক্ষিত থাকে।',
    'ob.sign.title':     'সাইন',
    'ob.sign.body':      'আপনার স্বাক্ষর আঁকুন বা টাইপ করুন, তারপর এটি রাখতে পৃষ্ঠায় ক্লিক করুন।',
    'ob.download.title': 'ডাউনলোড',
    'ob.download.body':  'আপনার সমস্ত পরিবর্তন সহ PDF সংরক্ষণ করুন। জটিল উৎস ফাইলগুলি র‍্যাস্টার রপ্তানিতে ফিরে যায় যাতে আপনি ফলাফল পেতে পারেন।',
    'ob.skip':           'এড়িয়ে যান',
    'ob.next':           'পরবর্তী',
    'ob.done':           'সম্পন্ন',
  },
  ru: {
    'tb.open':           'Открыть PDF',
    'tb.merge_pdf':      'Объединить с другим PDF',
    'tb.merge_at_start': 'Добавить в начало',
    'tb.merge_at_end':   'Добавить в конец',
    'tb.add_text':       'Добавить текст',
    'tb.edit_text':      'Редактировать текст',
    'tb.add_signature':  'Добавить подпись',
    'tb.add_image':      'Добавить изображение (только в этой сессии)',
    'tb.draw':           'Рисовать',
    'tb.select':         'Выделить',
    'tb.pen_settings':   'Настройки пера',
    'tb.insert_date':    'Вставить сегодняшнюю дату',
    'tb.profile':        'Профиль',
    'tb.undo':           'Отменить',
    'tb.clear_all':      'Очистить всё',
    'tb.download':       'Скачать',
    'tb.zoom_in':        'Увеличить',
    'tb.zoom_out':       'Уменьшить',
    'tb.reset_zoom':     'Сбросить масштаб',
    'tb.snap_on':        'Привязка вкл.',
    'tb.snap_off':       'Привязка выкл.',
    'tb.snap_to_lines':  'Привязка к найденным линиям формы',
    'tb.font_family':    'Шрифт',
    'tb.font_size':      'Размер шрифта',
    'tb.lang':           'Язык',
    'tb.theme':          'Тема',
    'es.heading':        'Заполнить и подписать PDF',
    'es.description':    'Перетащите PDF и заполните поля, разместите текст, поставьте подпись или нарисуйте от руки. Всё остаётся в вашем браузере — ничего не покидает ваше устройство.',
    'es.drag':           'Перетащите PDF сюда',
    'es.or_click':       'или нажмите в любом месте, чтобы выбрать файл',
    'es.tap':           'Коснитесь, чтобы выбрать PDF',
    'ob.add_text.body_touch': 'Коснитесь любого места страницы, чтобы поместить текстовое поле. Режим привязки автоматически выровняет его по найденным строкам формы.',
    'es.open_pdf':       'Открыть PDF',
    'es.try_sample':     'Попробовать образец формы',
    'es.recent':         'Недавние',
    'es.clear_recents':  'Очистить всё',
    'ob.welcome.title':  'Добро пожаловать в PDF Helper',
    'ob.welcome.body':   'Заполняйте, подписывайте, рисуйте и редактируйте PDF полностью в браузере. Ничего не покидает ваше устройство. Выберите ниже образец формы или откройте свою, чтобы начать.',
    'ob.add_text.title': 'Добавьте текст в любом месте',
    'ob.add_text.body':  'Нажмите в любом месте страницы, чтобы поместить текстовое поле. Режим привязки автоматически выровняет его по найденным строкам формы.',
    'ob.edit.title':     'Редактирование существующего текста PDF',
    'ob.edit.body':      'Нажмите на любое слово в PDF, чтобы переписать его на месте. Жирный шрифт, цвет, выравнивание и многострочная разметка сохраняются.',
    'ob.sign.title':     'Подпись',
    'ob.sign.body':      'Нарисуйте или введите свою подпись, затем нажмите на страницу, чтобы поместить её.',
    'ob.download.title': 'Скачать',
    'ob.download.body':  'Сохраните PDF со всеми вашими изменениями. Сложные исходные файлы откатываются на растровый экспорт, чтобы вы всё равно получили результат.',
    'ob.skip':           'Пропустить',
    'ob.next':           'Далее',
    'ob.done':           'Готово',
  },
  pt: {
    'tb.open':           'Abrir PDF',
    'tb.merge_pdf':      'Mesclar outro PDF',
    'tb.merge_at_start': 'Adicionar ao início',
    'tb.merge_at_end':   'Adicionar ao fim',
    'tb.add_text':       'Adicionar texto',
    'tb.edit_text':      'Editar texto',
    'tb.add_signature':  'Adicionar assinatura',
    'tb.add_image':      'Adicionar imagem (apenas nesta sessão)',
    'tb.draw':           'Desenhar',
    'tb.select':         'Selecionar',
    'tb.pen_settings':   'Configurações da caneta',
    'tb.insert_date':    'Inserir data de hoje',
    'tb.profile':        'Perfil',
    'tb.undo':           'Desfazer',
    'tb.clear_all':      'Limpar tudo',
    'tb.download':       'Baixar',
    'tb.zoom_in':        'Aumentar zoom',
    'tb.zoom_out':       'Diminuir zoom',
    'tb.reset_zoom':     'Redefinir zoom',
    'tb.snap_on':        'Encaixe ativado',
    'tb.snap_off':       'Encaixe desativado',
    'tb.snap_to_lines':  'Encaixar nas linhas detectadas do formulário',
    'tb.font_family':    'Fonte',
    'tb.font_size':      'Tamanho da fonte',
    'tb.lang':           'Idioma',
    'tb.theme':          'Tema',
    'es.heading':        'Preencher & assinar um PDF',
    'es.description':    'Solte um PDF e preencha seus campos, coloque texto, adicione uma assinatura ou desenhe à mão livre. Tudo fica no seu navegador — nada sai do seu dispositivo.',
    'es.drag':           'Arraste um PDF para cá',
    'es.or_click':       'ou clique em qualquer lugar para escolher um arquivo',
    'es.tap':           'Toque para escolher um PDF',
    'ob.add_text.body_touch': 'Toque em qualquer ponto da página para colocar uma caixa de texto. O modo de encaixe alinha-a automaticamente às linhas detectadas do formulário.',
    'es.open_pdf':       'Abrir PDF',
    'es.try_sample':     'Experimentar um formulário de exemplo',
    'es.recent':         'Recentes',
    'es.clear_recents':  'Limpar tudo',
    'ob.welcome.title':  'Bem-vindo ao PDF Helper',
    'ob.welcome.body':   'Preencha, assine, desenhe e edite PDFs inteiramente no seu navegador. Nada sai do seu dispositivo. Escolha um formulário de exemplo abaixo ou abra um próprio para começar.',
    'ob.add_text.title': 'Adicionar texto em qualquer lugar',
    'ob.add_text.body':  'Clique em qualquer ponto da página para colocar uma caixa de texto. O modo de encaixe alinha-a automaticamente às linhas detectadas do formulário.',
    'ob.edit.title':     'Editar texto existente do PDF',
    'ob.edit.body':      'Clique em qualquer palavra do PDF para reescrevê-la no lugar. Negrito, cor, alinhamento e layout multilinha são preservados.',
    'ob.sign.title':     'Assinar',
    'ob.sign.body':      'Desenhe ou digite sua assinatura e depois clique na página para colocá-la.',
    'ob.download.title': 'Baixar',
    'ob.download.body':  'Salve o PDF com todas as suas edições. Arquivos fonte complicados recorrem a uma exportação rasterizada para que você ainda obtenha um resultado.',
    'ob.skip':           'Pular',
    'ob.next':           'Próximo',
    'ob.done':           'Concluído',
  },
  id: {
    'tb.open':           'Buka PDF',
    'tb.merge_pdf':      'Gabungkan PDF lain',
    'tb.merge_at_start': 'Tambahkan di awal',
    'tb.merge_at_end':   'Tambahkan di akhir',
    'tb.add_text':       'Tambah teks',
    'tb.edit_text':      'Edit teks',
    'tb.add_signature':  'Tambah tanda tangan',
    'tb.add_image':      'Tambah gambar (sesi saat ini saja)',
    'tb.draw':           'Gambar',
    'tb.select':         'Pilih',
    'tb.pen_settings':   'Pengaturan pena',
    'tb.insert_date':    'Masukkan tanggal hari ini',
    'tb.profile':        'Profil',
    'tb.undo':           'Urungkan',
    'tb.clear_all':      'Hapus semua',
    'tb.download':       'Unduh',
    'tb.zoom_in':        'Perbesar',
    'tb.zoom_out':       'Perkecil',
    'tb.reset_zoom':     'Atur ulang zoom',
    'tb.snap_on':        'Snap aktif',
    'tb.snap_off':       'Snap nonaktif',
    'tb.snap_to_lines':  'Snap ke garis formulir yang terdeteksi',
    'tb.font_family':    'Font',
    'tb.font_size':      'Ukuran font',
    'tb.lang':           'Bahasa',
    'tb.theme':          'Tema',
    'es.heading':        'Isi & tanda tangan PDF',
    'es.description':    'Letakkan PDF dan isi bidangnya, tempatkan teks, tambahkan tanda tangan, atau gambar bebas. Semuanya tetap di browser Anda — tidak ada yang meninggalkan perangkat Anda.',
    'es.drag':           'Seret PDF ke sini',
    'es.or_click':       'atau klik di mana saja untuk memilih file',
    'es.tap':           'Ketuk untuk memilih PDF',
    'ob.add_text.body_touch': 'Ketuk di mana saja di halaman untuk meletakkan kotak teks. Mode snap menyelaraskannya otomatis dengan baris formulir yang terdeteksi.',
    'es.open_pdf':       'Buka PDF',
    'es.try_sample':     'Coba formulir contoh',
    'es.recent':         'Terbaru',
    'es.clear_recents':  'Hapus semua',
    'ob.welcome.title':  'Selamat datang di PDF Helper',
    'ob.welcome.body':   'Isi, tanda tangan, gambar, dan edit PDF sepenuhnya di browser Anda. Tidak ada yang meninggalkan perangkat Anda. Pilih formulir contoh di bawah atau buka milik Anda sendiri untuk mulai.',
    'ob.add_text.title': 'Tambahkan teks di mana saja',
    'ob.add_text.body':  'Klik di mana saja di halaman untuk meletakkan kotak teks. Mode snap menyelaraskannya otomatis dengan baris formulir yang terdeteksi.',
    'ob.edit.title':     'Edit teks PDF yang ada',
    'ob.edit.body':      'Klik kata apa saja di PDF untuk menulis ulang di tempat. Tebal, warna, perataan, dan tata letak multi-baris dipertahankan.',
    'ob.sign.title':     'Tanda tangan',
    'ob.sign.body':      'Gambar atau ketik tanda tangan Anda, lalu klik halaman untuk menempatkannya.',
    'ob.download.title': 'Unduh',
    'ob.download.body':  'Simpan PDF dengan semua perubahan. File sumber yang sulit beralih ke ekspor raster sehingga Anda tetap mendapatkan hasilnya.',
    'ob.skip':           'Lewati',
    'ob.next':           'Berikutnya',
    'ob.done':           'Selesai',
  },
  tr: {
    'tb.open':           'PDF aç',
    'tb.pages':          'Sayfalar',
    'tb.help':           'Yardım',
    'tb.merge_pdf':      'Başka bir PDF birleştir',
    'tb.merge_at_start': 'Başa ekle',
    'tb.merge_at_end':   'Sona ekle',
    'tb.add_text':       'Metin ekle',
    'tb.edit_text':      'Metni düzenle',
    'tb.redact':         'Karart',
    'tb.add_signature':  'İmza ekle',
    'tb.add_image':      'Görsel ekle (yalnızca oturum)',
    'tb.watermark':      'Filigran',
    'tb.draw':           'Çiz',
    'tb.select':         'Seç',
    'tb.insert_date':    'Bugünün tarihini ekle',
    'tb.profile':        'Profil',
    'tb.undo':           'Geri al',
    'tb.clear_all':      'Tümünü temizle',
    'tb.download':       'İndir',
    'tb.zoom_in':        'Yakınlaştır',
    'tb.zoom_out':       'Uzaklaştır',
    'tb.reset_zoom':     'Yakınlaştırmayı sıfırla',
    'tb.snap_on':        'Hizalama açık',
    'tb.snap_off':       'Hizalama kapalı',
    'tb.font_family':    'Yazı tipi',
    'tb.font_size':      'Yazı boyutu',
    'tb.lang':           'Dil',
    'tb.theme':          'Tema',
    'mb.text.label':      'Metin ekle',
    'mb.text.help':       'Metin kutusu yerleştirmek için sayfaya tıklayın.',
    'mb.redact.label':    'Karart',
    'mb.redact.help':     'Hassas içeriği opak bir blokla kapatmak için sürükleyin.',
    'mb.exit':            'Çık',
    'es.heading':        'PDF doldur ve imzala',
    'es.description':    'PDF yükleyin; alanları doldurun, metin ekleyin, imza koyun veya serbestçe çizin. Her şey tarayıcınızda kalır.',
    'es.drag':           'PDF dosyasını buraya sürükleyin',
    'es.or_click':       'veya dosya seçmek için tıklayın',
    'es.tap':           'PDF seçmek için dokunun',
    'ob.add_text.body_touch': 'Metin kutusu bırakmak için sayfanın herhangi bir yerine dokunun. Hizalama açıkken kutu dokunduğunuz form satırına otomatik hizalanır.',
    'es.open_pdf':       'PDF aç',
    'es.recent':         'Son kullanılanlar',
    'es.clear_recents':  'Tümünü temizle',
    'ob.welcome.title':  'PDF Helper’a hoş geldiniz',
    'ob.welcome.body':   'PDF’leri tamamen tarayıcınızda doldurun, imzalayın, düzenleyin, birleştirin ve yeniden sıralayın.',
    'ob.add_text.title': 'Her yere metin ekleyin',
    'ob.edit.title':     'Mevcut PDF metnini düzenleyin',
    'ob.sign.title':     'İmzala',
    'ob.download.title': 'İndir',
    'ob.skip':           'Atla',
    'ob.next':           'İleri',
    'ob.back':           'Geri',
    'ob.done':           'Bitti',
  },
  vi: {
    'tb.open':           'Mở PDF',
    'tb.pages':          'Trang',
    'tb.help':           'Trợ giúp',
    'tb.merge_pdf':      'Gộp PDF khác',
    'tb.merge_at_start': 'Thêm vào đầu',
    'tb.merge_at_end':   'Thêm vào cuối',
    'tb.add_text':       'Thêm văn bản',
    'tb.edit_text':      'Sửa văn bản',
    'tb.redact':         'Che nội dung',
    'tb.add_signature':  'Thêm chữ ký',
    'tb.add_image':      'Thêm ảnh (chỉ phiên này)',
    'tb.watermark':      'Hình mờ',
    'tb.draw':           'Vẽ',
    'tb.select':         'Chọn',
    'tb.insert_date':    'Chèn ngày hôm nay',
    'tb.profile':        'Hồ sơ',
    'tb.undo':           'Hoàn tác',
    'tb.clear_all':      'Xóa tất cả',
    'tb.download':       'Tải xuống',
    'tb.zoom_in':        'Phóng to',
    'tb.zoom_out':       'Thu nhỏ',
    'tb.reset_zoom':     'Đặt lại thu phóng',
    'tb.snap_on':        'Căn dòng bật',
    'tb.snap_off':       'Căn dòng tắt',
    'tb.font_family':    'Phông chữ',
    'tb.font_size':      'Cỡ chữ',
    'tb.lang':           'Ngôn ngữ',
    'tb.theme':          'Giao diện',
    'mb.text.label':      'Thêm văn bản',
    'mb.text.help':       'Nhấp vào trang để đặt hộp văn bản.',
    'mb.redact.label':    'Che nội dung',
    'mb.redact.help':     'Kéo qua nội dung nhạy cảm để phủ bằng khối đặc.',
    'mb.exit':            'Thoát',
    'es.heading':        'Điền & ký PDF',
    'es.description':    'Thả PDF vào, điền trường, đặt văn bản, thêm chữ ký hoặc vẽ tự do. Mọi thứ ở lại trong trình duyệt của bạn.',
    'es.drag':           'Kéo PDF vào đây',
    'es.or_click':       'hoặc nhấp để chọn tệp',
    'es.tap':           'Chạm để chọn tệp PDF',
    'ob.add_text.body_touch': 'Chạm vào bất kỳ đâu trên trang để đặt hộp văn bản. Khi bật căn dòng, hộp sẽ tự căn vào hàng biểu mẫu bạn chạm.',
    'es.open_pdf':       'Mở PDF',
    'es.recent':         'Gần đây',
    'es.clear_recents':  'Xóa tất cả',
    'ob.welcome.title':  'Chào mừng đến với PDF Helper',
    'ob.welcome.body':   'Điền, ký, sửa, gộp và sắp xếp lại PDF hoàn toàn trong trình duyệt.',
    'ob.add_text.title': 'Thêm văn bản ở bất cứ đâu',
    'ob.edit.title':     'Sửa văn bản PDF có sẵn',
    'ob.sign.title':     'Ký',
    'ob.download.title': 'Tải xuống',
    'ob.skip':           'Bỏ qua',
    'ob.next':           'Tiếp',
    'ob.back':           'Quay lại',
    'ob.done':           'Xong',
  },
  th: {
    'tb.open':           'เปิด PDF',
    'tb.pages':          'หน้า',
    'tb.help':           'ช่วยเหลือ',
    'tb.merge_pdf':      'รวม PDF อื่น',
    'tb.merge_at_start': 'เพิ่มไว้ต้นไฟล์',
    'tb.merge_at_end':   'เพิ่มไว้ท้ายไฟล์',
    'tb.add_text':       'เพิ่มข้อความ',
    'tb.edit_text':      'แก้ไขข้อความ',
    'tb.redact':         'ปิดทับ',
    'tb.add_signature':  'เพิ่มลายเซ็น',
    'tb.add_image':      'เพิ่มรูปภาพ (เฉพาะเซสชัน)',
    'tb.watermark':      'ลายน้ำ',
    'tb.draw':           'วาด',
    'tb.select':         'เลือก',
    'tb.insert_date':    'ใส่วันที่วันนี้',
    'tb.profile':        'โปรไฟล์',
    'tb.undo':           'เลิกทำ',
    'tb.clear_all':      'ล้างทั้งหมด',
    'tb.download':       'ดาวน์โหลด',
    'tb.zoom_in':        'ซูมเข้า',
    'tb.zoom_out':       'ซูมออก',
    'tb.reset_zoom':     'รีเซ็ตซูม',
    'tb.snap_on':        'จัดแนวเปิด',
    'tb.snap_off':       'จัดแนวปิด',
    'tb.font_family':    'แบบอักษร',
    'tb.font_size':      'ขนาดตัวอักษร',
    'tb.lang':           'ภาษา',
    'tb.theme':          'ธีม',
    'mb.text.label':      'เพิ่มข้อความ',
    'mb.text.help':       'คลิกบนหน้าเพื่อวางกล่องข้อความ',
    'mb.redact.label':    'ปิดทับ',
    'mb.redact.help':     'ลากทับข้อมูลสำคัญเพื่อปิดด้วยบล็อกทึบ',
    'mb.exit':            'ออก',
    'es.heading':        'กรอกและเซ็น PDF',
    'es.description':    'วาง PDF แล้วกรอกช่อง เพิ่มข้อความ ใส่ลายเซ็น หรือวาดได้อย่างอิสระ ทุกอย่างอยู่ในเบราว์เซอร์ของคุณ',
    'es.drag':           'ลาก PDF มาที่นี่',
    'es.or_click':       'หรือคลิกเพื่อเลือกไฟล์',
    'es.tap':           'แตะเพื่อเลือกไฟล์ PDF',
    'ob.add_text.body_touch': 'แตะที่ใดก็ได้บนหน้าเพื่อวางกล่องข้อความ เมื่อเปิดจัดแนว กล่องจะจัดเข้ากับแถวฟอร์มที่คุณแตะโดยอัตโนมัติ',
    'es.open_pdf':       'เปิด PDF',
    'es.recent':         'ล่าสุด',
    'es.clear_recents':  'ล้างทั้งหมด',
    'ob.welcome.title':  'ยินดีต้อนรับสู่ PDF Helper',
    'ob.welcome.body':   'กรอก เซ็น แก้ไข รวม และจัดเรียง PDF ใหม่ได้ทั้งหมดในเบราว์เซอร์',
    'ob.add_text.title': 'เพิ่มข้อความได้ทุกที่',
    'ob.edit.title':     'แก้ไขข้อความเดิมใน PDF',
    'ob.sign.title':     'เซ็น',
    'ob.download.title': 'ดาวน์โหลด',
    'ob.skip':           'ข้าม',
    'ob.next':           'ถัดไป',
    'ob.back':           'กลับ',
    'ob.done':           'เสร็จสิ้น',
  },
  ko: {
    'tb.open':           'PDF 열기',
    'tb.pages':          '페이지',
    'tb.help':           '도움말',
    'tb.merge_pdf':      '다른 PDF 병합',
    'tb.merge_at_start': '앞에 추가',
    'tb.merge_at_end':   '뒤에 추가',
    'tb.add_text':       '텍스트 추가',
    'tb.edit_text':      '텍스트 편집',
    'tb.redact':         '가리기',
    'tb.add_signature':  '서명 추가',
    'tb.add_image':      '이미지 추가(세션 전용)',
    'tb.watermark':      '워터마크',
    'tb.draw':           '그리기',
    'tb.select':         '선택',
    'tb.insert_date':    '오늘 날짜 삽입',
    'tb.profile':        '프로필',
    'tb.undo':           '실행 취소',
    'tb.clear_all':      '모두 지우기',
    'tb.download':       '다운로드',
    'tb.zoom_in':        '확대',
    'tb.zoom_out':       '축소',
    'tb.reset_zoom':     '확대/축소 초기화',
    'tb.snap_on':        '맞춤 켜짐',
    'tb.snap_off':       '맞춤 꺼짐',
    'tb.font_family':    '글꼴',
    'tb.font_size':      '글자 크기',
    'tb.lang':           '언어',
    'tb.theme':          '테마',
    'mb.text.label':      '텍스트 추가',
    'mb.text.help':       '페이지를 클릭해 텍스트 상자를 배치하세요.',
    'mb.redact.label':    '가리기',
    'mb.redact.help':     '민감한 내용을 드래그해 불투명한 블록으로 덮습니다.',
    'mb.exit':            '나가기',
    'es.heading':        'PDF 작성 및 서명',
    'es.description':    'PDF를 넣고 필드를 채우거나 텍스트, 서명, 자유 그리기를 추가하세요. 모든 작업은 브라우저 안에서만 이루어집니다.',
    'es.drag':           'PDF를 여기에 끌어다 놓기',
    'es.or_click':       '또는 클릭하여 파일 선택',
    'es.tap':           '탭하여 PDF 선택',
    'ob.add_text.body_touch': '페이지 어디든 탭해 텍스트 상자를 놓으세요. 맞춤이 켜져 있으면 탭한 양식 줄에 자동으로 정렬됩니다.',
    'es.open_pdf':       'PDF 열기',
    'es.recent':         '최근 파일',
    'es.clear_recents':  '모두 지우기',
    'ob.welcome.title':  'PDF Helper에 오신 것을 환영합니다',
    'ob.welcome.body':   '브라우저 안에서 PDF를 작성, 서명, 편집, 병합, 재정렬할 수 있습니다.',
    'ob.add_text.title': '어디에나 텍스트 추가',
    'ob.edit.title':     '기존 PDF 텍스트 편집',
    'ob.sign.title':     '서명',
    'ob.download.title': '다운로드',
    'ob.skip':           '건너뛰기',
    'ob.next':           '다음',
    'ob.back':           '뒤로',
    'ob.done':           '완료',
  },
  it: {
    'tb.open':           'Apri PDF',
    'tb.pages':          'Pagine',
    'tb.help':           'Aiuto',
    'tb.merge_pdf':      'Unisci un altro PDF',
    'tb.merge_at_start': 'Aggiungi all’inizio',
    'tb.merge_at_end':   'Aggiungi alla fine',
    'tb.add_text':       'Aggiungi testo',
    'tb.edit_text':      'Modifica testo',
    'tb.redact':         'Oscura',
    'tb.add_signature':  'Aggiungi firma',
    'tb.add_image':      'Aggiungi immagine (solo sessione)',
    'tb.watermark':      'Filigrana',
    'tb.draw':           'Disegna',
    'tb.select':         'Seleziona',
    'tb.insert_date':    'Inserisci la data di oggi',
    'tb.profile':        'Profilo',
    'tb.undo':           'Annulla',
    'tb.clear_all':      'Cancella tutto',
    'tb.download':       'Scarica',
    'tb.zoom_in':        'Aumenta zoom',
    'tb.zoom_out':       'Riduci zoom',
    'tb.reset_zoom':     'Reimposta zoom',
    'tb.snap_on':        'Aggancio attivo',
    'tb.snap_off':       'Aggancio disattivo',
    'tb.font_family':    'Carattere',
    'tb.font_size':      'Dimensione testo',
    'tb.lang':           'Lingua',
    'tb.theme':          'Tema',
    'mb.text.label':      'Aggiungi testo',
    'mb.text.help':       'Fai clic su una pagina per posizionare una casella di testo.',
    'mb.redact.label':    'Oscura',
    'mb.redact.help':     'Trascina sul contenuto sensibile per coprirlo con un blocco opaco.',
    'mb.exit':            'Esci',
    'es.heading':        'Compila e firma un PDF',
    'es.description':    'Trascina un PDF, compila i campi, inserisci testo, aggiungi una firma o disegna a mano libera. Tutto resta nel tuo browser.',
    'es.drag':           'Trascina qui un PDF',
    'es.or_click':       'o fai clic per scegliere un file',
    'es.tap':           'Tocca per scegliere un PDF',
    'ob.add_text.body_touch': 'Tocca in qualsiasi punto della pagina per inserire una casella di testo. Con l’aggancio attivo, la casella si allinea automaticamente alla riga del modulo.',
    'es.open_pdf':       'Apri PDF',
    'es.recent':         'Recenti',
    'es.clear_recents':  'Cancella tutto',
    'ob.welcome.title':  'Benvenuto in PDF Helper',
    'ob.welcome.body':   'Compila, firma, modifica, unisci e riordina PDF interamente nel browser.',
    'ob.add_text.title': 'Aggiungi testo ovunque',
    'ob.edit.title':     'Modifica il testo PDF esistente',
    'ob.sign.title':     'Firma',
    'ob.download.title': 'Scarica',
    'ob.skip':           'Salta',
    'ob.next':           'Avanti',
    'ob.back':           'Indietro',
    'ob.done':           'Fine',
  },
  zh: {
    'tb.open':           '打开 PDF',
    'tb.merge_pdf':      '合并另一个 PDF',
    'tb.merge_at_start': '添加到开头',
    'tb.merge_at_end':   '添加到末尾',
    'tb.add_text':       '添加文本',
    'tb.edit_text':      '编辑文本',
    'tb.add_signature':  '添加签名',
    'tb.add_image':      '添加图片（仅本次会话）',
    'tb.draw':           '绘制',
    'tb.select':         '选择',
    'tb.pen_settings':   '画笔设置',
    'tb.insert_date':    '插入今日日期',
    'tb.profile':        '个人资料',
    'tb.undo':           '撤销',
    'tb.clear_all':      '全部清除',
    'tb.download':       '下载',
    'tb.zoom_in':        '放大',
    'tb.zoom_out':       '缩小',
    'tb.reset_zoom':     '重置缩放',
    'tb.snap_on':        '对齐已开启',
    'tb.snap_off':       '对齐已关闭',
    'tb.snap_to_lines':  '对齐检测到的表单行',
    'tb.font_family':    '字体',
    'tb.font_size':      '字号',
    'tb.lang':           '语言',
    'tb.theme':          '主题',
    'theme.system':      '系统',
    'theme.light':       '浅色',
    'theme.dark':        '深色',
    'theme.sepia':       '棕褐色',
    'theme.hc':          '高对比度',
    'theme.solarized':   'Solarized',
    'theme.dracula':     'Dracula',
    'mb.text.label':      '添加文本',
    'mb.text.help':       '点击页面以放置文本框。',
    'mb.signature.label': '放置签名',
    'mb.signature.help':  '点击页面以放置签名。',
    'mb.select.label':    '选择',
    'mb.select.help':     '点击项目以拖动、调整大小或删除。',
    'mb.draw.label':      '绘制',
    'mb.draw.help':       '在页面上点击并拖动以绘制笔画。',
    'mb.image.label':     '放置图片',
    'mb.image.help':      '点击页面以放置图片。图片仅在本次会话中——不会随 PDF 保存。',
    'mb.edit.label':      '编辑文本',
    'mb.edit.help':       '点击 PDF 中的任意文字以原地重写。',
    'mb.esc':             '按 Esc 退出',
    'mb.exit':            '退出',
    'es.heading':        '填写并签署 PDF',
    'es.description':    '拖入 PDF 文件以填写字段、放置文本、添加签名或自由绘制。一切都保留在浏览器中——文件不会离开您的设备。',
    'es.drag':           '将 PDF 拖到此处',
    'es.or_click':       '或点击任意位置选择文件',
    'es.tap':           '点按以选择 PDF',
    'ob.add_text.body_touch': '点按页面上任意位置以放置文本框。对齐模式会自动将其与检测到的表单行对齐。',
    'es.open_pdf':       '打开 PDF',
    'es.try_sample':     '试用示例表单',
    'es.recent':         '最近使用',
    'es.clear_recents':  '全部清除',
    'ob.welcome.title':  '欢迎使用 PDF Helper',
    'ob.welcome.body':   '完全在浏览器中填写、签署、绘制和编辑 PDF。文件不会离开您的设备。请从下面选择一个示例表单或打开您自己的文件以开始。',
    'ob.add_text.title': '在任意位置添加文本',
    'ob.add_text.body':  '点击页面上任意位置以放置文本框。对齐模式会自动将其与检测到的表单行对齐。',
    'ob.edit.title':     '编辑现有 PDF 文本',
    'ob.edit.body':      '点击 PDF 中的任意文字即可原地重写。粗体、颜色、对齐方式以及多行布局均会保留。',
    'ob.sign.title':     '签署',
    'ob.sign.body':      '绘制或输入您的签名,然后点击页面将其放置。',
    'ob.download.title': '下载',
    'ob.download.body':  '保存包含所有更改的 PDF。对于复杂的源文件,会回退到光栅导出,以确保您仍能获得结果。',
    'ob.skip':           '跳过',
    'ob.next':           '下一步',
    'ob.done':           '完成',
  },
  ja: {
    'tb.open':           'PDF を開く',
    'tb.merge_pdf':      '別の PDF を結合',
    'tb.merge_at_start': '先頭に追加',
    'tb.merge_at_end':   '末尾に追加',
    'tb.add_text':       'テキストを追加',
    'tb.edit_text':      'テキストを編集',
    'tb.add_signature':  '署名を追加',
    'tb.add_image':      '画像を追加(セッションのみ)',
    'tb.draw':           '描画',
    'tb.select':         '選択',
    'tb.pen_settings':   'ペン設定',
    'tb.insert_date':    '今日の日付を挿入',
    'tb.profile':        'プロフィール',
    'tb.undo':           '元に戻す',
    'tb.clear_all':      'すべて消去',
    'tb.download':       'ダウンロード',
    'tb.zoom_in':        '拡大',
    'tb.zoom_out':       '縮小',
    'tb.reset_zoom':     'ズームをリセット',
    'tb.snap_on':        'スナップ オン',
    'tb.snap_off':       'スナップ オフ',
    'tb.snap_to_lines':  '検出された罫線にスナップ',
    'tb.font_family':    'フォント',
    'tb.font_size':      '文字サイズ',
    'tb.lang':           '言語',
    'tb.theme':          'テーマ',
    'theme.system':      'システム',
    'theme.light':       'ライト',
    'theme.dark':        'ダーク',
    'theme.sepia':       'セピア',
    'theme.hc':          'ハイコントラスト',
    'theme.solarized':   'Solarized',
    'theme.dracula':     'Dracula',
    'mb.text.label':      'テキストを追加',
    'mb.text.help':       'ページをクリックしてテキストボックスを配置します。',
    'mb.signature.label': '署名を配置',
    'mb.signature.help':  'ページをクリックして署名を配置します。',
    'mb.select.label':    '選択',
    'mb.select.help':     '項目をクリックして、ドラッグ・サイズ変更・削除できます。',
    'mb.draw.label':      '描画',
    'mb.draw.help':       'ページ上でクリックしてドラッグするとストロークを描画します。',
    'mb.image.label':     '画像を配置',
    'mb.image.help':      'ページをクリックして画像を配置します。画像はセッション内のみで、PDF には保存されません。',
    'mb.edit.label':      'テキストを編集',
    'mb.edit.help':       'PDF 内の任意の語をクリックして、その場で書き換えます。',
    'mb.esc':             'Esc で終了',
    'mb.exit':            '終了',
    'es.heading':        'PDF を入力 & 署名',
    'es.description':    'PDF をドロップしてフィールドを入力したり、テキストを置いたり、署名を追加したり、自由に描画できます。すべてブラウザ内で完結し、ファイルは端末を離れません。',
    'es.drag':           'PDF をここにドラッグ',
    'es.or_click':       'または任意の場所をクリックしてファイルを選択',
    'es.tap':           'タップして PDF を選択',
    'ob.add_text.body_touch': 'ページ上の任意の場所をタップしてテキストボックスを配置します。スナップモードを使用すると、検出されたフォーム行に自動的に位置合わせされます。',
    'es.open_pdf':       'PDF を開く',
    'es.try_sample':     'サンプルフォームを試す',
    'es.recent':         '最近開いた',
    'es.clear_recents':  'すべて削除',
    'ob.welcome.title':  'PDF Helper へようこそ',
    'ob.welcome.body':   'ブラウザ内で PDF への入力、署名、描画、編集が完結します。ファイルは端末を離れません。下のサンプルフォームを試すか、お手元のファイルを開いて始めてください。',
    'ob.add_text.title': 'どこにでもテキストを追加',
    'ob.add_text.body':  'ページ上の任意の場所をクリックしてテキストボックスを配置します。スナップモードを使用すると、検出されたフォーム行に自動的に位置合わせされます。',
    'ob.edit.title':     '既存の PDF テキストを編集',
    'ob.edit.body':      'PDF 内のどの単語でもクリックして、その場で書き換えられます。太字、色、配置、複数行レイアウトはそのまま保持されます。',
    'ob.sign.title':     '署名',
    'ob.sign.body':      '署名を描くか入力し、ページをクリックして配置します。',
    'ob.download.title': 'ダウンロード',
    'ob.download.body':  'すべての変更を反映した PDF を保存します。難しいソースファイルはラスター出力にフォールバックするため、結果を確実に取得できます。',
    'ob.skip':           'スキップ',
    'ob.next':           '次へ',
    'ob.done':           '完了',
  },
  es: {
    'tb.open':           'Abrir PDF',
    'tb.merge_pdf':      'Fusionar otro PDF',
    'tb.merge_at_start': 'Añadir al inicio',
    'tb.merge_at_end':   'Añadir al final',
    'tb.add_text':       'Añadir texto',
    'tb.edit_text':      'Editar texto',
    'tb.add_signature':  'Añadir firma',
    'tb.add_image':      'Añadir imagen (solo sesión)',
    'tb.draw':           'Dibujar',
    'tb.select':         'Seleccionar',
    'tb.pen_settings':   'Ajustes del lápiz',
    'tb.insert_date':    'Insertar la fecha de hoy',
    'tb.profile':        'Perfil',
    'tb.undo':           'Deshacer',
    'tb.clear_all':      'Borrar todo',
    'tb.download':       'Descargar',
    'tb.zoom_in':        'Acercar',
    'tb.zoom_out':       'Alejar',
    'tb.reset_zoom':     'Restablecer zoom',
    'tb.snap_on':        'Ajuste activado',
    'tb.snap_off':       'Ajuste desactivado',
    'tb.snap_to_lines':  'Ajustar a las líneas del formulario',
    'tb.font_family':    'Fuente',
    'tb.font_size':      'Tamaño de letra',
    'tb.lang':           'Idioma',
    'tb.theme':          'Tema',
    'theme.system':      'Sistema',
    'theme.light':       'Claro',
    'theme.dark':        'Oscuro',
    'theme.sepia':       'Sepia',
    'theme.hc':          'Alto contraste',
    'theme.solarized':   'Solarized',
    'theme.dracula':     'Dracula',
    'mb.text.label':      'Añadir texto',
    'mb.text.help':       'Haz clic en una página para colocar un cuadro de texto.',
    'mb.signature.label': 'Colocar firma',
    'mb.signature.help':  'Haz clic en una página para colocar tu firma.',
    'mb.select.label':    'Seleccionar',
    'mb.select.help':     'Haz clic en un elemento para arrastrarlo, redimensionarlo o eliminarlo.',
    'mb.draw.label':      'Dibujar',
    'mb.draw.help':       'Haz clic y arrastra en una página para dibujar un trazo.',
    'mb.image.label':     'Colocar imagen',
    'mb.image.help':      'Haz clic en una página para colocar tu imagen. Las imágenes son solo para esta sesión — no se guardan con el PDF.',
    'mb.edit.label':      'Editar texto',
    'mb.edit.help':       'Haz clic en cualquier palabra del PDF para sustituirla en su lugar.',
    'mb.esc':             'Esc para salir',
    'mb.exit':            'Salir',
    'es.heading':        'Rellenar & firmar un PDF',
    'es.description':    'Arrastra un PDF, rellena sus campos, coloca texto, una firma o dibuja a mano alzada. Todo se queda en tu navegador — nada sale de tu dispositivo.',
    'es.drag':           'Arrastra un PDF aquí',
    'es.or_click':       'o haz clic en cualquier sitio para elegir un archivo',
    'es.tap':           'Toca para elegir un PDF',
    'ob.add_text.body_touch': 'Toca en cualquier punto de la página para colocar un cuadro de texto. El modo de ajuste lo alinea automáticamente con las filas del formulario.',
    'es.open_pdf':       'Abrir PDF',
    'es.try_sample':     'Probar un formulario de muestra',
    'es.recent':         'Recientes',
    'es.clear_recents':  'Borrar todo',
    'ob.welcome.title':  'Bienvenido a PDF Helper',
    'ob.welcome.body':   'Rellena, firma, dibuja y edita PDF completamente en tu navegador. Nada sale de tu dispositivo. Elige un formulario de muestra a continuación o abre el tuyo para empezar.',
    'ob.add_text.title': 'Añadir texto en cualquier lugar',
    'ob.add_text.body':  'Haz clic en cualquier punto de la página para colocar un cuadro de texto. El modo de ajuste lo alinea automáticamente con las filas del formulario.',
    'ob.edit.title':     'Editar texto del PDF',
    'ob.edit.body':      'Haz clic en cualquier palabra del PDF para sustituirla. Se conservan negrita, color, alineación y diseño multilínea.',
    'ob.sign.title':     'Firmar',
    'ob.sign.body':      'Dibuja o escribe tu firma y luego haz clic en la página para colocarla.',
    'ob.download.title': 'Descargar',
    'ob.download.body':  'Guarda el PDF con todos los cambios incorporados. Los archivos fuente complicados recurren a una exportación rasterizada para que aun así obtengas un resultado.',
    'ob.skip':           'Omitir',
    'ob.next':           'Siguiente',
    'ob.done':           'Hecho',
  },
} as const satisfies Record<Lang, Partial<Record<string, string>>>

export type TranslationKey = keyof typeof baseTranslations.en
export type TranslationVars = Record<string, string | number>

export const TRANSLATION_KEYS = Object.keys(baseTranslations.en) as TranslationKey[]

type OwnTranslationMap = Partial<Record<TranslationKey, string>>
type CompletionLang = Exclude<Lang, 'en'>
type CompletionMap = Partial<Record<CompletionLang, OwnTranslationMap>>

let translationCompletion: CompletionMap = {}
let translationCompletionPromise: Promise<void> | null = null

export function hasTranslationCompletion(lang: Lang): boolean {
  return lang === 'en' || !!translationCompletion[lang as CompletionLang]
}

export async function ensureTranslationCompletion(lang: Lang): Promise<void> {
  if (hasTranslationCompletion(lang)) return
  translationCompletionPromise ??= import('./i18nCompletion').then((module) => {
    translationCompletion = module.translationCompletion
  }).catch((err) => {
    // e.g. a stale tab requesting a chunk a newer deploy removed. Allow a
    // retry on the next language change instead of caching the failure.
    translationCompletionPromise = null
    throw err
  })
  await translationCompletionPromise
}

export function ownTranslationsForTest(lang: Lang): OwnTranslationMap {
  if (lang === 'en') return baseTranslations.en
  return {
    ...((baseTranslations as Partial<Record<Lang, OwnTranslationMap>>)[lang] ?? {}),
    ...(translationCompletion[lang as Exclude<Lang, 'en'>] ?? {}),
  }
}

function interpolate(template: string, vars?: TranslationVars): string {
  if (!vars) return template
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
    const value = vars[name]
    return value === undefined ? match : String(value)
  })
}

export function translate(key: string, lang: Lang, vars?: TranslationVars): string {
  const own = ownTranslationsForTest(lang)
  const template = own[key as TranslationKey]
    ?? baseTranslations.en[key as TranslationKey]
    ?? key
  return interpolate(template, vars)
}

export function formatNumber(lang: Lang, value: number, options?: Intl.NumberFormatOptions): string {
  try {
    return new Intl.NumberFormat(lang, options).format(value)
  } catch {
    return new Intl.NumberFormat('en', options).format(value)
  }
}

export function formatPercent(lang: Lang, value: number): string {
  return formatNumber(lang, value, {
    style: 'percent',
    maximumFractionDigits: 0,
  })
}
